import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import { File } from '../../features/types'

interface Props {
    open: boolean
    onClose: () => void
    files: File[] | null
}

const ImageViewerDialog: React.FC<Props> = ({ open, onClose, files }) => {
    const theme = useTheme()
    const [selected, setSelected] = useState<File | null>(null)
    const componentStyles = getStyles(theme)

    const [scale, setScale] = useState(1)
    const [origin, setOrigin] = useState({ x: 50, y: 50 })

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault()
        const rect = (e.target as HTMLElement).getBoundingClientRect()
        const offsetX = ((e.clientX - rect.left) / rect.width) * 100
        const offsetY = ((e.clientY - rect.top) / rect.height) * 100

        setOrigin({ x: offsetX, y: offsetY })
        setScale(prev => Math.min(5, Math.max(1, prev - e.deltaY * 0.001)))
    }

    const handleDoubleClick = () => setScale(1)




    useEffect(() => {
        if (!selected) return
        const handleEscape = (e: KeyboardEvent) => e.key === 'Escape' && setSelected(null)
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [selected])


    if (!files?.length) return null

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth PaperProps={{ sx: { maxHeight: '95vh', maxWidth: '60vw' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Файлы студента ({files.length})
                    <IconButton onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={componentStyles.content}
                >
                    {files.map((file) => (
                        <Box
                            key={file.id}
                            sx={componentStyles.fileItem}
                        >
                            {file.title && <Typography variant="subtitle2" fontWeight={600}>{file.title}</Typography>}
                            <Box
                                onClick={() => setSelected(file)}
                                sx={componentStyles.imageContainer}
                            >
                                <Box
                                    component="img"
                                    src={file.link}
                                    alt={file.title}
                                    sx={componentStyles.image}
                                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                                />
                                <Box
                                    className="zoom-icon"
                                    sx={componentStyles.zoomIcon}
                                >
                                    <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>

            {selected && (
                <Box
                    onClick={() => setSelected(null)}
                    sx={componentStyles.overlay}
                >
                    <IconButton
                        onClick={() => setSelected(null)}
                        sx={componentStyles.closeButton}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selected.title && (
                        <Typography
                            variant="h6"
                            sx={componentStyles.titleOverlay}
                        >
                            {selected.title}
                        </Typography>
                    )}
                    <Box
                        component="img"
                        src={selected.link}
                        alt={selected.title}
                        onClick={(e) => e.stopPropagation()}
                        onWheel={handleWheel}
                        onDoubleClick={handleDoubleClick}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: 1,

                            transform: `scale(${scale})`,
                            transformOrigin: `${origin.x}% ${origin.y}%`,
                            transition: 'transform 0.2s ease',
                            cursor: scale > 1 ? 'zoom-out' : 'zoom-in',
                        }}
                    />



                </Box>
            )}
        </>
    )
}

export default ImageViewerDialog

const getStyles = (theme: any) => ({
    content: {
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        overflowY: 'auto',
    },
    fileItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        pb: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    imageContainer: {
        position: 'relative',
        cursor: 'zoom-in',
        '&:hover .zoom-icon': { opacity: 1 },
    },
    image: {
        maxWidth: '100%',
        maxHeight: '80vh',
        objectFit: 'contain',
        borderRadius: 1,
        display: 'block',
        margin: '0 auto',
        '&:hover': { opacity: 0.9 },
    },
    zoomIcon: {
        position: 'absolute',
        top: 8,
        right: 8,
        p: 0.5,
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.6)',
        opacity: 0,
        transition: 'opacity 0.2s',
    },
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        zIndex: theme.zIndex.modal + 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom out',
        p: 2,
    },
    closeButton: {
        position: 'absolute',
        top: 16,
        right: 16,
        color: 'white',
        backgroundColor: 'rgba(0,0,0,0.5)',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
    },
    titleOverlay: {
        position: 'absolute',
        top: 16,
        left: 16,
        color: 'white',
        backgroundColor: 'rgba(0,0,0,0.5)',
        px: 2,
        py: 1,
        borderRadius: 1,
    },
    zoomedImage: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        borderRadius: 1,
    },
})